import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheck, ArrowLeft, AlertTriangle, Users, Search } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

// Editor detalhado de uma role: carrega as permissões reais do banco e salva por diff (add/remove).
export const RoleEditor = ({ roleName, onBack, onSaved }) => {
  const [catalog, setCatalog] = useState([]);
  const [sensitive, setSensitive] = useState([]);
  const [original, setOriginal] = useState([]);
  const [selected, setSelected] = useState([]);
  const [info, setInfo] = useState(null);
  const [q, setQ] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [{ data: cat }, { data: role }] = await Promise.all([
      api.get('/permissions/catalog'), api.get(`/roles/${encodeURIComponent(roleName)}`),
    ]);
    setCatalog(cat.modules || []);
    setSensitive(cat.sensitive || []);
    setOriginal(role.permissions || []);
    setSelected(role.permissions || []);
    setInfo(role);
  }, [roleName]);
  useEffect(() => { load(); }, [load]);

  const added = useMemo(() => selected.filter((p) => !original.includes(p)), [selected, original]);
  const removed = useMemo(() => original.filter((p) => !selected.includes(p)), [selected, original]);
  const dirty = added.length > 0 || removed.length > 0;
  const touchesSensitive = [...added, ...removed].some((p) => sensitive.includes(p));

  const toggle = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const persist = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/roles/${encodeURIComponent(roleName)}/permissions`, { add: added, remove: removed });
      toast({ title: 'Permissões salvas', description: `${data.added.length} adicionada(s) · ${data.removed.length} removida(s)` });
      setConfirmOpen(false);
      await load();
      onSaved && onSaved();
    } catch (e) { toast({ title: 'Erro ao salvar', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };

  const save = () => (touchesSensitive ? setConfirmOpen(true) : persist());

  if (!info) return <p className="ent-empty">Carregando permissões da role...</p>;

  const term = q.trim().toLowerCase();
  const modules = catalog.map((m) => ({
    ...m,
    groups: m.groups.map((g) => ({ ...g, permissions: g.permissions.filter((p) => !term || p.label.toLowerCase().includes(term) || p.key.includes(term)) }))
      .filter((g) => g.permissions.length),
  })).filter((m) => m.groups.length);

  return (
    <div className="space-y-3" data-testid="role-editor">
      <div className="flex items-center gap-3 flex-wrap">
        <button data-testid="role-editor-back" onClick={onBack} className="ent-btn"><ArrowLeft className="w-3.5 h-3.5" /> Roles</button>
        <div>
          <p className="font-display font-black text-lg uppercase tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary" /> {info.name}
          </p>
          <p className="text-[11px] t-label flex items-center gap-1.5">
            <Users className="w-3 h-3" /> {info.user_count} usuário(s) · {selected.length} de {catalog.reduce((a, m) => a + m.groups.reduce((b, g) => b + g.permissions.length, 0), 0)} permissões
          </p>
        </div>
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
          <input data-testid="role-perm-search" className="ent-input pl-8 w-[220px]" placeholder="Buscar permissão..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {dirty && (
        <div className="flex items-center gap-3 flex-wrap border border-[hsl(var(--warn))]/40 bg-[hsl(var(--warn))]/[0.06] rounded-md px-3 py-2" data-testid="role-unsaved-bar">
          <span className="ent-tag t-warn">Alterações não salvas</span>
          <span className="text-[11.5px] text-foreground/80">
            {added.length ? `+${added.length} adicionada(s)` : ''}{added.length && removed.length ? ' · ' : ''}{removed.length ? `−${removed.length} removida(s)` : ''}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="role-cancel" onClick={() => setSelected(original)} className="ent-btn">Cancelar</button>
            <button data-testid="role-save" onClick={save} disabled={saving} className="ent-btn ent-btn-primary">{saving ? 'Salvando...' : 'Salvar alterações'}</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {modules.map((m) => (
          <div key={m.module} className="ent-panel p-3 space-y-3" data-testid={`role-module-${m.module}`}>
            <p className="ent-label">{m.label}</p>
            {m.groups.map((g) => (
              <div key={g.group} className="space-y-1.5">
                <p className="text-[11px] font-semibold text-foreground/70">{g.group}</p>
                {g.permissions.map((p) => (
                  <label key={p.key} data-testid={`perm-row-${p.key}`}
                    className="flex items-start gap-2 text-[13px] cursor-pointer hover:bg-muted/30 rounded px-1.5 py-1 -mx-1.5 transition-colors">
                    <input type="checkbox" data-testid={`perm-${p.key}`} checked={selected.includes(p.key)}
                      onChange={() => toggle(p.key)} className="accent-blue-500 mt-0.5" />
                    <span className="flex-1">
                      {p.label}
                      {p.sensitive && <span className="ent-tag t-bad ml-1.5">sensível</span>}
                      {!p.known && <span className="ent-tag ml-1.5">legacy</span>}
                      <span className="block mono text-[10px] t-label">{p.key}</span>
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md" data-testid="role-sensitive-dialog">
          <DialogHeader><DialogTitle className="font-display font-bold text-lg uppercase tracking-tight flex items-center gap-2"><AlertTriangle className="w-4 h-4 t-bad" /> Permissões sensíveis</DialogTitle></DialogHeader>
          <div className="space-y-2 text-[13px]">
            <p>Você está alterando permissões sensíveis da role <b>{info.name}</b> ({info.user_count} usuário(s) afetado(s)):</p>
            {added.filter((p) => sensitive.includes(p)).map((p) => <p key={p} className="t-ok">+ {p} (Disabled → Enabled)</p>)}
            {removed.filter((p) => sensitive.includes(p)).map((p) => <p key={p} className="t-bad">− {p} (Enabled → Disabled)</p>)}
          </div>
          <DialogFooter>
            <button data-testid="role-sensitive-cancel" onClick={() => setConfirmOpen(false)} className="ent-btn">Voltar</button>
            <button data-testid="role-sensitive-confirm" onClick={persist} disabled={saving} className="ent-btn ent-btn-primary">{saving ? 'Salvando...' : 'Confirmar e salvar'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleEditor;
